import {
    AudienceId,
    formatDateNoTime,
    parseEntityIdSafe,
    TenantCredentialId,
} from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { CrmTable, CrmTableProps } from "components/table/crm-table";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton, primaryClasses } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import {
    GetAudiencesQuery,
    GetTenantCredentialsQuery,
    TenantCredentialStatus,
    TenantCredentialType,
    useCreateAudienceMutation,
    useDeleteTenantCredentialMutation,
    useGetAudiencesQuery,
    useGetTenantCredentialsQuery,
} from "generated/graphql";
import React from "react";
import { navigate } from "store/actions";
import { useRegionId } from "store/selectors/hooks";
import { AudienceLink } from "./audience-link";
import { AudienceShow } from "./audience-show";

interface MailchimpProps {
    match?: {
        path?: string;
        params?: {
            audienceId?: string;
            subtab?: string;
            audienceTab?: string;
        };
    };
}

export const Mailchimp: React.FC<MailchimpProps> = props => {
    const { match } = props;
    const regionId = useRegionId();

    const createAudienceMutation = useCreateAudienceMutation();
    const credentialsQuery = useGetTenantCredentialsQuery();

    const onNewAudience = React.useCallback(
        async (e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e?.preventDefault();

            const audience = await createAudienceMutation.mutateAsync({
                input: {
                    name: "New Contact List",
                    regionId,
                },
            });

            navigate(
                `/settings/integrations/mailchimp/audiences/${audience.createAudience.id}?initialMode=edit`,
            );
        },
        [createAudienceMutation, regionId],
    );

    const audienceId = match?.params?.audienceId
        ? parseEntityIdSafe<AudienceId>(match.params.audienceId).getOrElse(null)
        : null;

    const subtab = match?.params?.subtab ? match.params.subtab : null;
    const audienceTab = match?.params?.audienceTab ? match.params.audienceTab : null;

    return (
        <>
            {audienceId ? (
                <AudienceShow audienceId={audienceId} subtab={audienceTab} />
            ) : null}
            {subtab ? (
                <ContentFull>
                    <ContentHeader>Mailchimp</ContentHeader>
                    <QueryRenderer query={credentialsQuery} name="Mailchimp">
                        {data => {
                            const mailchimpCredential = data.getTenantCredentials.find(
                                credential =>
                                    credential.type === TenantCredentialType.Mailchimp,
                            );

                            return (
                                <>
                                    <MailchimpIntegration
                                        credential={mailchimpCredential}
                                        refetchCredentials={credentialsQuery.refetch}
                                    />
                                    {subtab === "audiences" ? (
                                        <MailchimpAudiencesSection
                                            onCreate={onNewAudience}
                                            isConnected={
                                                mailchimpCredential?.status ===
                                                TenantCredentialStatus.Active
                                            }
                                        />
                                    ) : null}
                                </>
                            );
                        }}
                    </QueryRenderer>
                </ContentFull>
            ) : null}
        </>
    );
};

interface MailchimpAudiencesSectionProps {
    onCreate: () => void;
    isConnected: boolean;
}

const MailchimpAudiencesSection: React.FC<MailchimpAudiencesSectionProps> = props => {
    const { onCreate, isConnected } = props;

    if (!isConnected) {
        return null;
    }

    return <AudiencesList onCreate={onCreate} />;
};

interface AudiencesListProps {
    onCreate: () => void;
}

const AudiencesList: React.FC<AudiencesListProps> = props => {
    const { onCreate } = props;
    const regionId = useRegionId();
    const audiencesQuery = useGetAudiencesQuery({
        input: {
            regionId,
        },
    });
    const audiences = audiencesQuery.data?.getAudiences ?? [];

    const data = audiences;

    const handleCreate = React.useCallback(
        async (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
            e.preventDefault();
            onCreate();
        },
        [onCreate],
    );

    const columns: CrmTableProps<typeof data[0]>["columns"] = [
        {
            key: "name",
            header: "Name",
            renderCell: ({
                record,
            }: {
                record: GetAudiencesQuery["getAudiences"][0];
            }) => {
                return <AudienceLink audience={record} />;
            },
        },
        {
            key: "updatedAt",
            header: "Last updated",
            renderCell: ({
                record,
            }: {
                record: GetAudiencesQuery["getAudiences"][0];
            }) => {
                return <span>{formatDateNoTime(record.updatedAt)}</span>;
            },
        },
    ];

    if (data.length === 0) {
        return (
            <div>
                <div className="p-2 space-y-4">
                    <p>
                        Create a contact list from criteria you define across clients,
                        communities, and referral contacts.
                    </p>
                    <PrimaryButton onClick={handleCreate}>
                        Create Contact List
                    </PrimaryButton>
                </div>
            </div>
        );
    }

    return (
        <div>
            <CrmTable
                data={data}
                columns={columns}
                initialSort={[{ id: "name", desc: false }]}
            />
            <div className="mt-4">
                <PrimaryButton onClick={handleCreate}>Add new contact list</PrimaryButton>
            </div>
        </div>
    );
};

interface MailchimpIntegrationProps {
    credential?: GetTenantCredentialsQuery["getTenantCredentials"][0];
    refetchCredentials: () => void;
}

const MailchimpIntegration: React.FC<MailchimpIntegrationProps> = props => {
    const { credential: mailchimpCredential, refetchCredentials } = props;
    const deleteTenantCredentialMutation = useDeleteTenantCredentialMutation();

    const handleDisconnect = React.useCallback(
        async (credentialId: TenantCredentialId) => {
            const confirmed = window.confirm(
                "Are you sure you want to disconnect Mailchimp? This will remove access to your Mailchimp account and stop all syncing.",
            );

            if (confirmed) {
                await deleteTenantCredentialMutation.mutateAsync({
                    id: credentialId,
                });
                refetchCredentials();
            }
        },
        [deleteTenantCredentialMutation, refetchCredentials],
    );

    return (
        <div className="mb-6">
            {mailchimpCredential ? (
                <div className="space-y-2">
                    {mailchimpCredential.status === TenantCredentialStatus.Active ? (
                        <>
                            <InlineBanner type="success">
                                Mailchimp is connected. You can now manage contact lists
                                and sync them with Mailchimp.
                            </InlineBanner>
                            <div>
                                <SecondaryButton
                                    onClick={() =>
                                        handleDisconnect(mailchimpCredential.id)
                                    }
                                    disabled={deleteTenantCredentialMutation.isLoading}>
                                    {deleteTenantCredentialMutation.isLoading
                                        ? "Disconnecting..."
                                        : "Disconnect Mailchimp"}
                                </SecondaryButton>
                            </div>
                        </>
                    ) : (
                        <>
                            <InlineBanner type="error">
                                Mailchimp is no longer connected. Please click{" "}
                                <strong>Reconnect Mailchimp</strong> below to reconnect.
                            </InlineBanner>
                            <div>
                                <a href="/api/auth/mailchimp" className={primaryClasses}>
                                    Reconnect Mailchimp
                                </a>
                            </div>
                        </>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    <div>
                        Connect your Mailchimp account to sync contact lists and manage
                        your email marketing campaigns.
                    </div>
                    <div>
                        <a href="/api/auth/mailchimp" className={primaryClasses}>
                            Connect to Mailchimp
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};
